import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ListItemIcon, Menu, MenuItem, Pagination, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CheckIcon from '@mui/icons-material/Check'
import FilterListIcon from '@mui/icons-material/FilterList'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded'
import DangerousRoundedIcon from '@mui/icons-material/DangerousRounded'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Palette } from 'src/feature/theme/model/Palette'
import { useMyTeamStore } from '../../store/my-team.store'
import { SkillsSearchBar } from './SkillsSearchBar'
import { TeamUsedSkillGapBox } from './TeamUsedSkillGapBox'

const PAGE_SIZE = 10
const DEBOUNCE_DELAY = 1500

export type TeamUsedSkillsProps = {
  onItemClick?: (itemName: string) => void
}

/**
 * Displays all used skills with search and sorting capabilities.
 */
export const TeamUsedSkills = observer(({ onItemClick }: TeamUsedSkillsProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const { isLoading } = myTeamStore.getGenericFieldsState('getAllUsedSkills')

  const [showGap, setShowGap] = useState(false)
  const [pagelength, setPageLength] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [skillSort, setSkillSort] = useState<'asc' | 'multi'>('asc')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const sortMenuRef = useRef(null)
  const prevSearchTextRef = useRef('')
  
  const pageTranslation = translation.pages.myTeam

  const skillsData = useMemo(() => myTeamStore.allUsedSkills, [myTeamStore.allUsedSkills])
  const skillsCount = useMemo(() => skillsData?.count || 0, [skillsData?.count])
  const hasSkills = useMemo(() => skillsCount > 0, [skillsCount])
  const showPagination = useMemo(() => skillsCount > PAGE_SIZE, [skillsCount])

  const legendItems = useMemo(() => [
    { color: 'text.primary', label: pageTranslation.metricsHeader.meetsExpectations(), icon: <CheckRoundedIcon sx={{ fontSize: 14 }} /> },
    { color: Palette.DANGER, label: pageTranslation.metricsHeader.belowExpectations(), icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} /> },
    { color: Palette.OLIVE_GREEN, label: pageTranslation.metricsHeader.exceedsExpectations(), icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} /> },
    { color: 'primary.main', label: pageTranslation.sortHeader.validated(), icon: <VerifiedUserRoundedIcon sx={{ fontSize: 14 }} /> },
    { color: Palette.GOLDEN_ORANGE, label: pageTranslation.sortHeader.notValidated(), icon: <DangerousRoundedIcon sx={{ fontSize: 14 }} /> },
  ], [pageTranslation])

  const skillSortOptions = useMemo(() => [
    { value: 'asc', label: pageTranslation.sortHeader.alphabetical() },
    { value: 'multi', label: pageTranslation.sortHeader.multiple() },
  ], [pageTranslation])

  useEffect(() => {
    if (prevSearchTextRef.current !== searchText) {
      setPageLength(0)
      prevSearchTextRef.current = searchText
    }
  }, [searchText])
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        await myTeamStore.getAllUsedSkills(pagelength, PAGE_SIZE, searchText, skillSort)
        setShowGap(true)
      } catch (error) {
        setShowGap(false)
      }
    }
    
    fetchData()
  }, [searchText, pagelength, skillSort, myTeamStore])

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setPageLength((page - 1) * PAGE_SIZE)
  }, [])

  const handleSkillSortChange = useCallback((value: 'asc' | 'multi') => {
    setPageLength(0)
    setSkillSort(value)
    setShowSortMenu(false)
  }, [])

  const handleToggleSortMenu = useCallback(() => {
    setShowSortMenu(prev => !prev)
  }, [])

  const renderSkills = useMemo(() => {
    if (!skillsData?.skillSets) {
      return null
    }

    return skillsData.skillSets.map(skill => (
      <TeamUsedSkillGapBox key={skill.ids[0]} skillSet={skill} onItemClick={onItemClick} />
    ))
  }, [skillsData?.skillSets, onItemClick])

  return (
    <AppPaper sx={{ padding: '10px 15px', borderRadius: '10px', gap: 20, width: '100%', minHeight: 500 }}>
      <AppBox sx={{ width: '100%', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <AppBox sx={{ flexDirection: 'column', gap: 3 }}>
          <Typography variant='h4'>{pageTranslation.metricsHeader.usedSkills()}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Show all used skills</Typography>
          <AppBox sx={{ flexDirection: 'row', gap: 5, alignItems: 'center' }}>
            {legendItems.map(({ color, label, icon }) => (
              <AppBox key={label} sx={{ gap: 5, alignItems: 'center', justifyContent: 'center' }}>
                <AppBox sx={{ ...styles.iconBox, color }}>{icon}</AppBox>
                <Typography sx={{ fontSize: 11 }}>{label}</Typography>
              </AppBox>
            ))}
          </AppBox>
        </AppBox>
        <AppBox sx={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          <SkillsSearchBar
            count={skillsCount || '0'}
            value={searchText}
            onDebouncedChange={setSearchText}
            debounceDelay={DEBOUNCE_DELAY}
          />
          <AppBox ref={sortMenuRef} sx={styles.sortButton} onClick={handleToggleSortMenu}>
            <FilterListIcon fontSize="small" />
            {showSortMenu ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </AppBox>
          <Menu
            anchorEl={sortMenuRef.current}
            open={showSortMenu}
            onClose={handleToggleSortMenu}
          >
            {skillSortOptions.map(({ value, label }) => (
              <MenuItem key={value} onClick={() => handleSkillSortChange(value as 'asc' | 'multi')} sx={{ fontSize: 12 }}>
                <ListItemIcon>
                  {skillSort === value && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}
                </ListItemIcon>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </AppBox>
      </AppBox>
      <AppBox sx={{ width: '100%', flexDirection: 'column', gap: 10, flex: 1, justifyContent: !isLoading && showGap && !hasSkills ? 'center' : 'flex-start', alignItems: !isLoading && showGap && !hasSkills ? 'center' : 'stretch' }}>
        {!isLoading && showGap && hasSkills && renderSkills}
        {isLoading && (
          <AppSkeleton
            count={PAGE_SIZE}
            direction="column"
            rects={[
              { width: '100%', height: 25, color: 'background.dark' },
            ]}
          />
        )}
        {!isLoading && showGap && !hasSkills && <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>{translation.noData()}</Caption>}
      </AppBox>
      {showPagination && (
        <AppBox alignItems='center' justifyContent='center' width='100%'>
          <Pagination
            shape='rounded'
            color='primary'
            count={Math.ceil(skillsCount / PAGE_SIZE)}
            page={Math.floor(pagelength / PAGE_SIZE) + 1}
            showFirstButton
            showLastButton
            onChange={handlePageChange}
            sx={{ '& .MuiPaginationItem-root': { fontSize: 12, height: 20 } }}
          />
        </AppBox>
      )}
    </AppPaper>
  )
})

const styles = {
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sortButton: {
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
  },
}